import { useRef } from "react";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { EMAIL, IMG, LINKS } from "../../data/site";
import { CTA, Eyebrow, Photo, Reveal, useParallax } from "./primitives";
import { MOBILE_GLOW, SWAP_EYEBROW, SWAP_TITLE } from "./PhotoBand";

export default function Closing({
  id = "contact",
  eyebrow = "Work with Jake",
  title = "Begin where you are.",
  text = "Private sessions, massage & bodywork, breathwork, meditation and hypnosis. Reach out to schedule a call or ask a question.",
  img = IMG.palms,
  primary = { label: "Book a Session", href: LINKS.contact },
  secondary = null,
  showEmail = true,
  testid = "contact-section",
}) {
  const ref = useRef(null);
  const y = useParallax(ref, ["-10%", "10%"]);
  return (
    <section ref={ref} id={id} aria-labelledby={`${id}-title`} data-testid={testid} className="relative flex min-h-[640px] scroll-mt-24 items-end overflow-hidden bg-ink lg:min-h-[88vh] lg:items-center">
      <motion.div style={{ y }} className="absolute inset-x-0 -inset-y-[12%]">
        <Photo img={img} sizes="100vw" className="h-full w-full object-cover" />
      </motion.div>
      <div aria-hidden className="absolute inset-0 bg-[linear-gradient(to_top,rgba(28,24,20,0.88),rgba(28,24,20,0.25)_65%)] lg:bg-[linear-gradient(to_right,rgba(28,24,20,0.78),rgba(28,24,20,0.15)_70%)]" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-20 text-ivory sm:px-8 lg:px-12">
        <Reveal className="relative isolate max-w-xl">
          {img === IMG.coastline && <div aria-hidden className={MOBILE_GLOW} />}
          <Eyebrow tone="dark" className={img === IMG.coastline ? SWAP_EYEBROW : ""}>{eyebrow}</Eyebrow>
          <h2 id={`${id}-title`} className={`mt-5 font-display text-[2.8rem] font-light leading-[1.02] sm:text-6xl lg:text-7xl ${img === IMG.coastline ? SWAP_TITLE : ""}`}>
            {title}
          </h2>
          <p className="mt-6 text-base leading-relaxed text-ivory/85 sm:text-lg">{text}</p>
        </Reveal>
        <Reveal delay={0.15} className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          <CTA href={primary.href} variant="coral" testid={`${id}-primary-button`}>
            {primary.label}
          </CTA>
          {secondary && (
            <CTA href={secondary.href} variant="outlineLight" testid={`${id}-secondary-button`}>
              {secondary.label}
            </CTA>
          )}
          {showEmail && (
            <a
              href={LINKS.email}
              data-testid={`${id}-email-link`}
              className="inline-flex min-h-[48px] items-center justify-center gap-2.5 rounded-full border border-ivory/40 px-6 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-ivory hover:text-ink"
            >
              <Mail aria-hidden className="h-4 w-4" />
              {EMAIL}
            </a>
          )}
        </Reveal>
      </div>
    </section>
  );
}
